import React, { useEffect, useState } from 'react';

import { css } from '@emotion/react';
import { useAuth } from '@opendoor/auth-fe';
import { getCSSVariable, PressableBox, Text } from '@opendoor/bricks-next';
import { Box } from '@opendoor/bricks/core';
import { AnimatePresence, motion } from 'framer-motion';

import { getSessionExtras } from 'components/api';

import { useT } from 'helpers/i18n';
import { useObservability } from 'helpers/observability';
import { useHasActiveBuyerOffers } from 'helpers/useBuyerOffers';
import { useLeads } from 'helpers/useLeads';

import type { MenuConfig, NavigationProps } from '../../types';
import { LanguageToggle } from '../LanguageToggle';
import { boxVariants, navList } from '../menuAnimations';
import { MobileMenuList } from '../mobile/MobileMenuList';
import { MobileMenuSubMenu } from '../mobile/MobileMenuSubMenu';
import { MenuCTA } from './MenuCTA';

interface MenuContentProps extends NavigationProps, MenuConfig {
  isVisible: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  onAnimationComplete: (definition: any) => void;
  displayLanguageToggle?: boolean;
}

export const MenuContent: React.FC<MenuContentProps> = ({
  isVisible,
  containerRef,
  onAnimationComplete,
  slug,
  menuPanelNavLinks,
  mainNavLinks,
  ctaLink,
  ctaTitle,
  displayLanguageToggle,
  megaNavData,
}) => {
  const { authentication, logout, user: human } = useAuth();
  const { trackEvent } = useObservability();
  const { t } = useT();
  const isLoggedIn = authentication.state === 'authenticated';
  const leads = useLeads();
  const { hasActiveOffers } = useHasActiveBuyerOffers(true);
  const [hasResaleDashboard, setHasResaleDashboard] = useState(false);

  useEffect(() => {
    if (!human) {
      return;
    }
    getSessionExtras().then((extras) => {
      setHasResaleDashboard(extras.has_resale_closing_dashboard);
    });
  }, [human?.humanToken, human?.customerUuid]);

  const handleSignOut = () => {
    trackEvent('cta-click', 'account-signout', undefined, { categoryOverride: 'homepage' });
    logout();
  };

  const animate = isVisible ? 'visible' : 'hidden';
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);

  const languageToggleContent = displayLanguageToggle ? (
    <Box
      pl="24px"
      pr="24px"
      // Figma requires full-width toggle with 14px radius and equal halves.
      // bricks-next Toggle doesn't expose these via props, so we override its internals.
      // Fragile: depends on Toggle's DOM structure (button > div children).
      css={css`
        & button {
          width: 100%;
          border-radius: 14px !important;
        }
        & button > div:first-child {
          width: 50% !important;
        }
        & button > div:first-child > div {
          border-radius: 14px !important;
        }
        & button > div:not(:first-child) {
          flex: 1 0 0 !important;
          border-radius: 14px !important;
        }
      `}
    >
      <LanguageToggle />
    </Box>
  ) : null;

  const signOutContent = isLoggedIn ? (
    <Box display="flex" justifyContent="center" pt="16px" pb="16px" pl="12px" pr="12px">
      <PressableBox
        onPress={handleSignOut}
        aria-label="Sign out"
        analyticsName="cosmos-account-menu-sign-out"
        display="inline-flex"
        alignItems="center"
      >
        <Text tag="span" typography="$bodyMedium" color="$contentTertiary">
          {t('homepage.account_menu.sign_out', 'Sign out')}
        </Text>
      </PressableBox>
    </Box>
  ) : null;

  const menuFooter = (
    <Box
      css={css`
        border-top: 1px solid ${getCSSVariable('$borderPrimary')};
      `}
      pt="16px"
    >
      {signOutContent}
      {languageToggleContent}
      <MenuCTA ctaLink={ctaLink} ctaTitle={ctaTitle} animate={animate} />
    </Box>
  );

  return (
    <Box
      onAnimationComplete={onAnimationComplete}
      as={motion.div}
      variants={boxVariants}
      initial="hidden"
      animate={isVisible ? 'visible' : 'hidden'}
      exit="hidden"
      tabIndex={-1}
      ref={containerRef}
      backgroundColor="white0"
      gridArea="1 / 1 / -1 / -1"
      pt={72}
      pl={0}
      pr={0}
      pb={0}
      maxW={['100%', null, '424px']}
      bottom="0px"
      left={[0, null, 'auto']}
      right="0"
      position="fixed"
      top="0px"
      w="100%"
      zIndex="10"
      role="dialog"
      aria-labelledby="dialog-main-menu"
      aria-modal="true"
      display="grid"
      gridTemplateRows="1fr"
      gridTemplateColumns="1fr"
      overflow="auto"
      outline="0"
      css={css`
        overscroll-behavior-y: contain;
      `}
    >
      <Box
        as={motion.ul}
        animate={animate}
        variants={navList}
        gridColumn="1"
        gridRow="1"
        m="0"
        p="0"
        role="list"
        css={css`
          list-style: none;
          position: relative;
          overflow-y: auto;
          overflow-x: hidden;
        `}
      >
        <AnimatePresence initial={false}>
          {megaNavData && openSubMenu ? (
            <MobileMenuSubMenu
              key="submenu"
              megaNavData={megaNavData}
              openSubMenu={openSubMenu}
              navList={navList}
              onBack={() => setOpenSubMenu(null)}
              footer={menuFooter}
            />
          ) : (
            <MobileMenuList
              key="mainmenu"
              megaNavData={megaNavData}
              menuPanelNavLinks={menuPanelNavLinks}
              mainNavLinks={mainNavLinks}
              slug={slug}
              onSubMenuOpen={(itemName) => setOpenSubMenu(itemName)}
              isLoggedIn={isLoggedIn}
              leads={leads}
              hasActiveOffers={hasActiveOffers}
              hasResaleDashboard={hasResaleDashboard}
              footer={menuFooter}
            />
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
};
